import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { X, Clock, Calendar, User } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import AppointmentViewModal from './AppointmentViewModal';
import '../ClientWeekViewMobile.css'; // On utilise les styles de base, surchargés par le Dashboard CSS

const ClientDashboardWeekView = React.memo(({ db, client, currentDate, formatDate, formatTime }) => {
    const [weekDates, setWeekDates] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [firstDayOfWeek, setFirstDayOfWeek] = useState(1);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const { theme } = useTheme();

    // 1. Configuration (Premier jour de la semaine)
    const fetchPlanningSettings = useCallback(async () => {
        if (!db) return;
        try {
            const settingsDoc = await getDoc(doc(db, 'settings', 'general'));
            if (settingsDoc.exists()) {
                const settings = settingsDoc.data();
                setFirstDayOfWeek(settings.firstDayOfWeek || 1);
            }
        } catch (error) {
            console.error("Erreur settings:", error);
        }
    }, [db]);

    useEffect(() => {
        fetchPlanningSettings();
    }, [fetchPlanningSettings]);

    // 2. Calcul des dates de la semaine
    const updateWeekDates = useCallback((date) => {
        const weekStart = new Date(date);
        const day = weekStart.getDay();
        const diff = (day < firstDayOfWeek ? 7 : 0) + day - firstDayOfWeek;
        weekStart.setDate(weekStart.getDate() - diff);
        const dates = [];
        for (let i = 0; i < 7; i++) {
            const day = new Date(weekStart);
            day.setDate(weekStart.getDate() + i);
            dates.push(day);
        }
        setWeekDates(dates);
    }, [firstDayOfWeek]);

    // 3. Récupération Employés (Juste pour l'affichage noms/couleurs)
    const fetchEmployees = useCallback(async () => {
        if (!db) return;
        try {
            const employeesRef = collection(db, 'employees');
            // On prend tout pour avoir les noms, le filtrage affichage se fera naturellement
            const querySnapshot = await getDocs(employeesRef);
            const fetchedEmployees = querySnapshot.docs.map(doc => ({
                userId: doc.id,
                ...doc.data(),
                color: doc.data().color || '#808080'
            }));
            setEmployees(fetchedEmployees);
        } catch (error) {
            console.error("Erreur employés:", error);
        }
    }, [db]);

    // 4. Récupération RDV (Seulement Client)
    const fetchAppointments = useCallback(async () => {
        if (!db || !client.id) return;
        try {
            const appointmentsRef = collection(db, 'appointments');
            const q = query(appointmentsRef, where('clientId', '==', client.id));
            const querySnapshot = await getDocs(q);

            const fetchedAppointments = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                date: doc.data().date?.toDate() || new Date(),
            }));

            // Trier par date puis heure
            fetchedAppointments.sort((a, b) => {
                const dateDiff = a.date - b.date;
                if (dateDiff !== 0) return dateDiff;
                return a.startTime.localeCompare(b.startTime);
            });

            // On ne garde que ceux qui sont assignés ou confirmés pour une vue propre
            // (Optionnel: on pourrait montrer les 'en attente' si voulu, mais pour "propre" on filtre souvent)
            // Ici on garde tout ce qui est retourné car c'est filtré par clientId
            setAppointments(fetchedAppointments);

        } catch (error) {
            console.error("Erreur RDV:", error);
        }
    }, [db, client.id]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    useEffect(() => {
        if (db && client.id) {
            updateWeekDates(currentDate || new Date());
            fetchAppointments();
        }
    }, [db, client.id, currentDate, fetchEmployees, fetchAppointments, updateWeekDates]);


    // Helpers
    const getEmployeeName = (employeeId) => {
        const employee = employees.find(emp => emp.userId === employeeId);
        if (employee) {
            return employee.name || `${employee.firstName} ${employee.lastName}`;
        }
        return "Non assigné";
    };

    const getEmployeeColor = (employeeId) => {
        const employee = employees.find(emp => emp.userId === employeeId);
        return employee?.color || '#808080';
    };

    const isToday = (date) => {
        const today = new Date();
        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    };

    const calculateDuration = (startTime, endTime) => {
        try {
            const [startHour, startMin] = startTime.split(':').map(Number);
            const [endHour, endMin] = endTime.split(':').map(Number);
            const durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
            const hours = Math.floor(durationMinutes / 60);
            const minutes = durationMinutes % 60;
            if (hours > 0 && minutes > 0) return `${hours}h${minutes}`;
            if (hours > 0) return `${hours}h`;
            return `${minutes}min`;
        } catch (e) { return ''; }
    };

    const getStatusBadge = (appointment) => {
        if (!appointment.employeeId) return { text: 'Non assigné', bgColor: '#fee2e2', textColor: '#991b1b' };
        if (appointment.status === 'assigne' || appointment.status === 'confirmed') return { text: 'Confirmé', bgColor: '#d1fae5', textColor: '#065f46' };
        if (appointment.status === 'cancelled') return { text: 'Annulé', bgColor: '#fee2e2', textColor: '#991b1b' };
        return { text: 'En attente', bgColor: '#fef3c7', textColor: '#92400e' };
    };

    const handleAppointmentClick = (appointment) => {
        setSelectedAppointment(appointment);
        setIsViewModalOpen(true);
    };

    // RENDU
    const renderAppointmentsForDay = (date) => {
        const dayAppointments = appointments.filter(app =>
            app.date.toDateString() === date.toDateString()
        );

        return (
            <div className="cwvm-day-column">
                {/* Header Jour Simplifié */}
                <div className="cwvm-day-header">
                    <div className="cwvm-day-name">
                        {formatDate(date, 'eee. d MMMM')}
                        {isToday(date) && <span className="cwvm-today-badge">Aujourd'hui</span>}
                    </div>
                </div>

                <div className="cwvm-appointments">
                    {dayAppointments.length === 0 ? (
                        <div className="py-3 text-center text-sm text-gray-500 italic bg-opacity-10">
                            Aucun rendez-vous
                        </div>
                    ) : (
                        dayAppointments.map((appointment, index) => {
                            const employeeColor = getEmployeeColor(appointment.employeeId);
                            const duration = calculateDuration(appointment.startTime, appointment.endTime);
                            const statusBadge = getStatusBadge(appointment);

                            return (
                                <div
                                    key={index}
                                    className="cwvm-appointment relative"
                                    style={{ borderLeftColor: employeeColor }}
                                    onClick={() => handleAppointmentClick(appointment)}
                                >
                                    {appointment.notes && appointment.notes.content && (
                                        <div className="absolute top-1 right-1">
                                            <span className="text-[10px] bg-purple-100 text-purple-800 px-1 rounded-full">
                                                📝
                                            </span>
                                        </div>
                                    )}

                                    <div className="cwvm-appointment-time">
                                        <div className="flex items-center">
                                            <Clock size={12} className="cwvm-icon" />
                                            {`${formatTime(appointment.startTime)} - ${formatTime(appointment.endTime)}`}
                                        </div>
                                        <span className="ml-2 text-gray-400 font-normal">{duration}</span>
                                    </div>

                                    <div className="cwvm-appointment-employee">
                                        <User size={12} className="cwvm-icon" />
                                        {appointment.employeeId ? getEmployeeName(appointment.employeeId) : "Non assigné"}
                                    </div>

                                    <div
                                        className="cwvm-appointment-status"
                                        style={{
                                            backgroundColor: statusBadge.bgColor,
                                            color: statusBadge.textColor
                                        }}
                                    >
                                        {statusBadge.text}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="cwvm-container client-dashboard-week-view">
            <div className="cwvm-week-view">
                {weekDates.map((date, index) => (
                    <React.Fragment key={index}>
                        {renderAppointmentsForDay(date)}
                    </React.Fragment>
                ))}
            </div>

            {isViewModalOpen && selectedAppointment && (
                <AppointmentViewModal
                    isOpen={isViewModalOpen}
                    onClose={() => setIsViewModalOpen(false)}
                    appointment={selectedAppointment}
                    client={client} // Pour le contexte, même si on a déjà les infos
                />
            )}
        </div>
    );
});

export default ClientDashboardWeekView;
